// Login Page - Parts4Drive (Unified Better Auth Architecture)
// -----------------------------------------------------------------------------
// Thin page shell around the shared components/auth/LoginForm — the SAME form
// the marketplace home page embeds in its modal. All sign-in logic (email,
// Google, device verification, post-auth routing) lives in that one component.
'use client'

import { Suspense, useEffect, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { Loader2, Home, ChevronRight } from 'lucide-react'
import { subscribeToAuth } from '@/lib/auth'
import BrandLogo from '@/components/BrandLogo'
import LoginForm from '@/components/auth/LoginForm'

function LoginShell() {
  const searchParams = useSearchParams()
  const [currentUser, setCurrentUser] = useState<any>(null)

  // Pre-fill email / return target if passed in query params
  // (e.g. from a 409 conflict redirect or ?redirect= from the seller SPA)
  const emailParam = searchParams.get('email') || ''
  const returnTo = searchParams.get('returnTo') || searchParams.get('redirect') || ''

  useEffect(() => {
    const unsubscribe = subscribeToAuth((session) => {
      setCurrentUser(session?.user || null)
    })
    return unsubscribe
  }, [])

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-slate-50 text-slate-800" data-theme="light">
      <div className="w-full max-w-md mx-auto bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-slate-200/80 max-h-[92vh] overflow-y-auto my-auto shrink-0">
        {/* Breadcrumb Navigation back to Home */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 mb-5 font-medium">
          <Link href="/" className="flex items-center gap-1 hover:text-[#0A1B4A] transition-colors">
            <Home className="h-3.5 w-3.5" />
            <span>Home</span>
          </Link>
          <ChevronRight className="h-3 w-3 text-slate-400" />
          <span className="text-[#0A1B4A] font-semibold">Sign In</span>
        </nav>

        <div className="flex flex-col items-center text-center mb-6">
          <Link
            href="/"
            className="size-16 rounded-2xl bg-[#0A1B4A]/5 p-2 flex items-center justify-center border border-slate-200/80 shadow-xs mb-3 hover:scale-105 transition-transform overflow-hidden cursor-pointer"
            title="Go to Homepage"
          >
            <BrandLogo type="icon" theme="light" className="size-full object-contain" />
          </Link>
          <h1 className="font-display text-2xl font-bold text-[#0A1B4A]">Sign in to Parts4Drive</h1>
          <p className="text-slate-500 text-xs font-medium mt-1">
            Access your automotive inventory &amp; Parts4Drive Marketplace operations
          </p>
        </div>

        <LoginForm
          returnTo={returnTo || undefined}
          defaultEmail={emailParam || undefined}
        />
        {currentUser?.email ? (
          <p className="text-center text-[11px] text-slate-400 mt-4 break-all">
            Signed in as {currentUser.email}
          </p>
        ) : null}
      </div>
    </div>
  )
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <Loader2 className="size-8 text-[#0A1B4A] animate-spin" />
        </div>
      }
    >
      <LoginShell />
    </Suspense>
  )
}
